import { CountryPartyType, WarehouseSyncPreviewItem } from "../../api/Api";
import {
  HubSyncValues,
  SyncEntityType,
  SyncRow,
  SyncableField,
  buildSyncRows,
  isActionable
} from "../../components/lifecycle/warehouseSync/utils";
import { CountryPartyPatches } from "../../components/lifecycle/warehouseSync/applyCountryPatch";
import { buildPatch } from "../../components/lifecycle/warehouseSync/utils";
import { COUNTRY_PARTY_LABELS } from "../countries/country/mftTasks/parties/utils";

// Re-exported so the page and the apply hook do not each reach into the shared component folder.
export type { SyncEntityType };

/**
 * One entity in the bulk sync table, with its differences already computed by the same
 * buildSyncRows the single-counterparty dialog uses. The endpoint ships raw hub and warehouse
 * values precisely so this comparison happens here, once, for both features.
 */
export type PreviewRow = {
  id: string;
  entityType: SyncEntityType;
  /** Countries only: which party of the country this row covers. */
  partyType?: CountryPartyType;
  name: string;
  /** Countries only: "TSO (Energinet)", for the second line under the country name. */
  partyLabel?: string;
  catalystId: string;
  syncedAt?: string | null;
  /** Empty when the catalyst id matched no warehouse row: nothing to compare against. */
  rows: SyncRow[];
  actionable: SyncRow[];
  foundInWarehouse: boolean;
};

/** Accepted fields per row key. Absent key means nothing accepted for it. */
export type Selection = Record<string, ReadonlySet<SyncableField>>;

/**
 * Selection and React keys are keyed by type + id + party, not id alone. Counterparty ids and broker
 * ids come from different books and are not guaranteed to be distinct, and a country deliberately
 * appears several times over, once per party, all under the same id.
 */
export const rowKey = (
  row: Pick<PreviewRow, "entityType" | "id" | "partyType">
) => `${row.entityType}:${row.id}:${row.partyType ?? ""}`;

/**
 * "TSO (Energinet)" once Market Access has named the party, plain "TSO" until then. The same shape
 * the country lifecycle dialogs label their party rows with, so the two screens read alike.
 */
const partyDisplayLabel = (
  partyType: CountryPartyType,
  partyName?: string | null
) => {
  const base = COUNTRY_PARTY_LABELS[partyType] ?? partyType;
  return partyName ? `${base} (${partyName})` : base;
};

export const buildPreviewRows = (
  items: WarehouseSyncPreviewItem[]
): PreviewRow[] =>
  items
    .filter((item) => !!item.id)
    .map((item) => {
      const hub: HubSyncValues = {
        kycCheck: item.hub?.kycCheck ?? "",
        creditCheck: item.hub?.creditCheck ?? "",
        creditAmount: item.hub?.creditAmount ?? undefined,
        creditCurrency: item.hub?.creditCurrency ?? ""
      };

      // Anything the backend does not tag as a broker or a country is a counterparty, which is also
      // what an older deployment of the endpoint returns.
      const entityType: SyncEntityType =
        item.entityType === "broker"
          ? "broker"
          : item.entityType === "country"
            ? "country"
            : "counterparty";

      const partyType = item.partyType ?? undefined;

      const rows = item.warehouse
        ? buildSyncRows(item.warehouse, hub, entityType, {
            // The backend knows which parties have a credit line to write into; the page must not
            // guess. Absent means yes, which is what the other two books are.
            supportsCredit: item.supportsCredit !== false
          })
        : [];

      return {
        id: item.id as string,
        entityType,
        partyType,
        name: item.counterpartyName || (item.id as string),
        partyLabel: partyType
          ? partyDisplayLabel(partyType, item.partyName)
          : undefined,
        catalystId: item.catalystId ?? "",
        syncedAt: item.syncedAt,
        rows,
        actionable: rows.filter(isActionable),
        foundInWarehouse: !!item.warehouse
      };
    });

/**
 * Whether a row is worth showing when the table is filtered down. Three things qualify: a real
 * difference somebody can accept, a Catalyst ID the warehouse does not recognise, and a value the
 * warehouse holds that we have no way to store.
 *
 * That last one is not actionable on this page, which is exactly why it has to be listed: nobody
 * will ever find an unrecognised assessment status by scrolling the unfiltered table, and hiding it
 * behind a filter called "needing attention" is the wrong way round.
 */
export const needsAttention = (row: PreviewRow) =>
  row.actionable.length > 0 ||
  !row.foundInWarehouse ||
  row.rows.some((syncRow) => syncRow.unsyncable?.kind === "attention");

/** Every real difference, pre-ticked, matching what the single-counterparty dialog does. */
export const selectAllActionable = (rows: PreviewRow[]): Selection =>
  rows.reduce<Selection>((selection, row) => {
    if (row.actionable.length) {
      selection[rowKey(row)] = new Set(row.actionable.map((r) => r.field));
    }
    return selection;
  }, {});

export const acceptedFor = (
  selection: Selection,
  row: PreviewRow
): ReadonlySet<SyncableField> =>
  selection[rowKey(row)] ?? new Set<SyncableField>();

/** Entities with at least one accepted field, i.e. what an apply would write to. */
export const acceptedRows = (rows: PreviewRow[], selection: Selection) =>
  rows.filter((row) => acceptedFor(selection, row).size > 0);

export const acceptedFieldCount = (rows: PreviewRow[], selection: Selection) =>
  rows.reduce((total, row) => total + acceptedFor(selection, row).size, 0);

export const toggleField = (
  selection: Selection,
  row: PreviewRow,
  field: SyncableField
): Selection => {
  const next = new Set(acceptedFor(selection, row));

  if (next.has(field)) {
    next.delete(field);
  } else {
    next.add(field);
  }

  return { ...selection, [rowKey(row)]: next };
};

/** All of an entity's differences on or off, for the leading row checkbox. */
export const toggleRow = (
  selection: Selection,
  row: PreviewRow,
  accept: boolean
): Selection => ({
  ...selection,
  [rowKey(row)]: accept
    ? new Set(row.actionable.map((r) => r.field))
    : new Set<SyncableField>()
});

/**
 * One write the apply sweep performs. Defined here rather than next to the mutation because it is
 * what the grouping below produces, and because both sides of that grouping are pure.
 */
export type ApplySyncVariables =
  | {
      id: string;
      entityType: "counterparty" | "broker";
      patch: Partial<HubSyncValues>;
    }
  | {
      id: string;
      entityType: "country";
      /** Accepted values per party. Every party named here gets its sync stamp. */
      partyPatches: CountryPartyPatches;
    };

export type ApplyJob = {
  /** What to name this write if it fails. The country name, not the party. */
  label: string;
  variables: ApplySyncVariables;
};

/**
 * Turns the accepted rows into the writes to perform, which is not one per row: a country appears
 * once per party but is a single document, so its accepted parties are merged into one job. Applying
 * them separately would be a read-modify-write, a queue message and a summary upsert per party, each
 * having to be sequenced against the last so it did not revert it.
 *
 * Jobs come back in the order their first row appears, so the progress bar advances down the table.
 */
export const buildApplyJobs = (
  rows: PreviewRow[],
  selection: Selection
): ApplyJob[] => {
  const jobs: ApplyJob[] = [];
  const countryJobs = new Map<string, ApplyJob>();

  acceptedRows(rows, selection).forEach((row) => {
    const patch = buildPatch(row.rows, acceptedFor(selection, row));

    if (row.entityType !== "country") {
      jobs.push({
        label: row.name,
        variables: { id: row.id, entityType: row.entityType, patch }
      });
      return;
    }

    // Keyed on the country id alone, so every accepted party of one country lands in one job.
    const existing = countryJobs.get(row.id);

    if (existing && existing.variables.entityType === "country") {
      existing.variables.partyPatches = {
        ...existing.variables.partyPatches,
        ...(row.partyType ? { [row.partyType]: patch } : {})
      };
      return;
    }

    const job: ApplyJob = {
      label: row.name,
      variables: {
        id: row.id,
        entityType: "country",
        partyPatches: row.partyType ? { [row.partyType]: patch } : {}
      }
    };

    countryJobs.set(row.id, job);
    jobs.push(job);
  });

  return jobs;
};
